import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Info } from 'lucide-react';
import GlowBackground from '../components/GlowBackground';
import Header from '../components/Header';
import PillSelector from '../components/PillSelector';
import InfoModal from '../components/InfoModal';
import RatingPopup from '../components/RatingPopup';
import { ValueMomentCard, SoftGateOverlay } from '../components/GuestMotivation';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { useGuestMotivation } from '../hooks/useGuestMotivation';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Daily-thought seen tracking ────────────────────────────────────────────
const getTodayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};
const hasDailyBeenSeenToday = () => {
  try {
    const raw = localStorage.getItem('rc_daily_seen');
    if (!raw) return false;
    return JSON.parse(raw).date === getTodayKey();
  } catch { return false; }
};
const markDailyAsSeen = () => {
  try {
    localStorage.setItem('rc_daily_seen', JSON.stringify({ date: getTodayKey() }));
  } catch {}
};

const Home = () => {
  const { user, isAuthenticated, updateUser } = useAuth();
  const modal = useModal();
  const motivation = useGuestMotivation(isAuthenticated);
  const pendingSoftGateAction = useRef(null);
  const initialFetchDone = useRef(false); // StrictMode guard — prevent double initial fetch
  useEffect(() => {
    const t = setTimeout(() => setShowScrollHint(true), 2800);
    return () => clearTimeout(t);
  }, []);

  const [thought, setThought] = useState(null);
  const [isDaily, setIsDaily] = useState(true);
  const [level, setLevel] = useState('spark');
  const [type, setType] = useState('general');
  const [isLoading, setIsLoading] = useState(false);
  const [types, setTypes] = useState([]);
  const [levels, setLevels] = useState([]);
  const [seenThoughts, setSeenThoughts] = useState(() => {
    try {
      const stored = localStorage.getItem('rc_seen_thoughts');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [dropFromTop, setDropFromTop] = useState(false);
  const [riseFromMiddle, setRiseFromMiddle] = useState(false);

  // Modal / overlay states (page-specific only)
  const [showTypeInfo, setShowTypeInfo] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [ratedThoughtId, setRatedThoughtId] = useState(null);
  const [showSoftGate, setShowSoftGate] = useState(false);

  // Track if we're on mobile to hide "What is this?" hint
  const [isDesktopOrTablet, setIsDesktopOrTablet] = useState(() => window.innerWidth >= 640);
  useEffect(() => {
    const handler = () => setIsDesktopOrTablet(window.innerWidth >= 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  // Show "What is this?" hint after a brief delay
  const [showScrollHint, setShowScrollHint] = useState(false);

  // ── Onboarding hint for new / guest users ────────────────────────────────
  const [showOnboardingHint, setShowOnboardingHint] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) {
      try {
        const dismissed = localStorage.getItem('rc_hint_dismissed');
        if (!dismissed) setShowOnboardingHint(true);
      } catch {}
    } else {
      setShowOnboardingHint(false);
    }
  }, [isAuthenticated]);

  const dismissHint = useCallback(() => {
    if (!showOnboardingHint) return;
    try { localStorage.setItem('rc_hint_dismissed', '1'); } catch {}
    setShowOnboardingHint(false);
  }, [showOnboardingHint]);

  // Fetch config on mount
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/config/types`),
      axios.get(`${API}/config/levels`)
    ]).then(([typesRes, levelsRes]) => {
      setTypes([
        { value: 'today', label: "Today's Thought" },
        ...typesRes.data.map(t => ({ value: t.key, label: t.label })),
      ]);
      setLevels(levelsRes.data.map(l => ({ value: l.key, label: l.label, description: l.description })));
    }).catch(() => {
      setTypes([
        { value: 'today', label: "Today's Thought" },
        { value: 'general', label: 'General' },
        { value: 'team', label: 'Team' },
      ]);
      setLevels([{ value: 'spark', label: 'Spark' }, { value: 'depth', label: 'Depth' }, { value: 'breakthrough', label: 'Breakthrough' }]);
    });
  }, []);

  // Fetch thought on mount — skip daily if already seen today
  useEffect(() => {
    if (initialFetchDone.current) return; // prevent StrictMode double-fire
    initialFetchDone.current = true;
    if (hasDailyBeenSeenToday()) {
      fetchRandomThought('spark', 'general', false, true); // skipTransition on first load
    } else {
      fetchDailyThought();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDailyThought = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/thoughts/daily`);
      setThought(response.data);
      setIsDaily(true);
      markDailyAsSeen();
      setSeenThoughts(prev => {
        const updated = prev.includes(response.data.id) ? prev : [...prev.slice(-49), response.data.id];
        try { localStorage.setItem('rc_seen_thoughts', JSON.stringify(updated)); } catch {}
        return updated;
      });
      setIsLoading(false);
    } catch {
      // No thought scheduled for today — fall through to general
      setIsLoading(false);
      setType('general');
      fetchRandomThought('spark', 'general');
    }
  };

  const fetchRandomThought = useCallback(async (newLevel, newType, triggerDrop = false, skipTransition = false) => {
    setIsLoading(true);

    if (!skipTransition) {
      setIsTransitioning(true);
      // Trigger drop glow (text disappearing)
      if (triggerDrop) {
        setDropFromTop(true);
        setTimeout(() => setDropFromTop(false), 100);
      }
    }

    try {
      const excludeIds = seenThoughts.slice(-50).join(',');
      
      const response = await axios.get(`${API}/thoughts/random`, {
        params: {
          level: newLevel || level,
          type: newType || type,
          exclude_ids: excludeIds || undefined
        }
      });
      
      const revealThought = () => {
        setThought(response.data);
        setIsDaily(false);
        setSeenThoughts(prev => {
          const updated = [...prev.slice(-49), response.data.id];
          try { localStorage.setItem('rc_seen_thoughts', JSON.stringify(updated)); } catch {}
          return updated;
        });
        setIsTransitioning(false);
        // Trigger glow rise at the same moment text appears
        if (!skipTransition) {
          setRiseFromMiddle(true);
          setTimeout(() => setRiseFromMiddle(false), 100);
        }
      };

      // Skip the 700ms hold when there is no current thought to transition away from
      if (skipTransition) {
        revealThought();
      } else {
        setTimeout(revealThought, 700);
      }
    } catch (error) {
      console.error('Error fetching random thought:', error);
      toast.error('Could not load next thought');
      setIsTransitioning(false);
    } finally {
      setIsLoading(false);
    }
  }, [level, type, seenThoughts]);

  const handleTalked = async () => {
    dismissHint();
    // Increment motivation counter (for unauthenticated users)
    motivation.incrementTalk();

    // Record points if user is authenticated
    if (isAuthenticated && thought) {
      try {
        const response = await axios.post(`${API}/points/complete-thought?thought_id=${thought.id}`);
        const { points_earned, total_points, level: newLevel, level_title, is_daily_bonus, is_streak_bonus } = response.data;
        
        let description = `+${points_earned} points`;
        if (is_daily_bonus) description += ' (Daily bonus!)';
        if (is_streak_bonus) description += ' (Streak bonus!)';
        
        toast.success('Great conversation!', {
          description,
          duration: 3000,
        });
        
        updateUser({ total_points, level: newLevel, level_title });
      } catch (error) {
        console.error('Error recording points:', error);
      }
    }
    
    // Show rating popup
    setRatedThoughtId(thought?.id);
    setShowRating(true);
  };

  const handleRatingSubmit = (score) => {
    if (ratedThoughtId) {
      axios.post(`${API}/thoughts/rate`, { thought_id: ratedThoughtId, score }).catch(() => {});
    }
  };

  const handleRatingClose = () => {
    setShowRating(false);
    setRatedThoughtId(null);

    // After the daily thought, revert type back to 'general' so random thoughts load correctly
    const nextType = type === 'today' ? 'general' : type;
    if (type === 'today') setType('general');

    if (motivation.needsSoftGate(motivation.thoughtCount)) {
      pendingSoftGateAction.current = () => fetchRandomThought(level, nextType, true);
      setShowSoftGate(true);
      return;
    }
    fetchRandomThought(level, nextType, true);
  };

  const handleNext = () => {
    dismissHint();
    const nextCount = motivation.thoughtCount + 1;

    if (isAuthenticated && thought) {
      axios.post(`${API}/user/skip-thought?thought_id=${thought.id}`).catch(() => {});
    }

    // After the daily thought, revert type to 'general'
    const nextType = type === 'today' ? 'general' : type;
    if (type === 'today') setType('general');

    if (motivation.needsSoftGate(nextCount)) {
      motivation.incrementThought();
      pendingSoftGateAction.current = () => fetchRandomThought(level, nextType, true);
      setShowSoftGate(true);
      return;
    }

    motivation.incrementThought();
    fetchRandomThought(level, nextType, true);
  };

  const handleSoftGatePass = () => {
    setShowSoftGate(false);
    motivation.passSoftGate();
    if (pendingSoftGateAction.current) {
      pendingSoftGateAction.current();
      pendingSoftGateAction.current = null;
    }
  };

  // Handle type change
  const handleTypeChange = (newType) => {
    dismissHint();
    setType(newType);
    if (newType === 'today') {
      fetchDailyThought();
    } else {
      fetchRandomThought(level, newType, true);
    }
  };

  // Handle level change — ignore when viewing the daily thought (level-agnostic)
  const handleLevelChange = (newLevel) => {
    dismissHint();
    setLevel(newLevel);
    if (type === 'today') return;
    fetchRandomThought(newLevel, type, true);
  };

  // Handle profile click
  const handleProfileClick = () => {
    modal.openProfile();
  };

  // Handle points click
  const handlePointsClick = () => {
    modal.openPoints();
  };

  return (
    <div style={{ backgroundColor: '#FFF8F0' }} data-testid="home-page">
      {/* ── App viewport ─────────────────────────────────────────── */}
      <div className="min-h-screen relative" style={{ overflow: 'clip' }}>
        {/* Glow background with drop/rise animations */}
        <GlowBackground dropFromTop={dropFromTop} riseFromMiddle={riseFromMiddle} />
      <div className="content-layer">
      {/* Header with menu handlers */}
      <Header 
        onProfileClick={handleProfileClick}
        onPointsClick={handlePointsClick}
      />
      
      {/* Main content */}
      <main className="min-h-screen flex flex-col items-center justify-center pt-20 pb-32 px-6 relative z-10">
        {/* Onboarding hint — guests / first-time visitors only */}
        <AnimatePresence>
          {showOnboardingHint && thought && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ delay: 0.45, duration: 0.75, ease: 'easeOut' }}
              className="mb-5 text-center px-4"
              data-testid="onboarding-hint"
            >
              <span
                className="sm:whitespace-nowrap"
                style={{
                  fontFamily: "'Caveat', cursive",
                  fontWeight: 600,
                  fontSize: 'clamp(1.15rem, 3.5vw, 1.45rem)',
                  color: 'rgba(44, 36, 23, 0.38)',
                  letterSpacing: '0.01em',
                  lineHeight: 1.35,
                  display: 'inline-block',
                  transform: 'rotate(-0.4deg)',
                }}
              >
                When your next conversation begins, start with this question:
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Daily thought indicator */}
        {isDaily && thought && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mb-4"
          >
            <span 
              className="text-xs tracking-[0.25em] uppercase text-orange-primary/60"
              data-testid="daily-thought-label"
            >
              Everyone's question today
            </span>
          </motion.div>
        )}

        {/* Thought Display */}
        <AnimatePresence mode="wait">
          {thought && (
            <motion.div
              key={thought.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: isTransitioning ? 0 : 1, y: isTransitioning ? -20 : 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="text-center max-w-4xl mb-8"
              data-testid="thought-display"
            >
              <h1 
                className="thought-text text-charcoal text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight"
                data-testid="thought-content"
              >
                {thought.content}
              </h1>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading state */}
        {!thought && !isTransitioning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div className="w-16 h-16 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin mx-auto mb-4" />
            <p className="text-charcoal/60 text-sm">Finding a thought for you...</p>
          </motion.div>
        )}

        {/* Action area - buttons or rating */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 flex flex-col items-center"
        >
          {showRating ? (
            <RatingPopup
              isOpen={showRating}
              onSubmit={handleRatingSubmit}
              onClose={handleRatingClose}
            />
          ) : (
            <>
              {/* Next thought */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mb-5"
              >
                <button
                  onClick={handleNext}
                  disabled={isLoading}
                  className="text-charcoal/50 hover:text-charcoal transition-colors duration-300 text-sm flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="next-thought-button"
                >
                  Give me another
                  <svg width="22" height="12" viewBox="0 0 22 12" fill="none" className="mt-px">
                    <path d="M2 6 C 6 6, 10 6, 15 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                    <path d="M13 3 L 17 6 L 13 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </button>
              </motion.div>

              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 8px 30px rgba(255, 120, 10, 0.25)' }}
                whileTap={{ scale: 0.98 }}
                onClick={handleTalked}
                disabled={isLoading}
                className="px-8 py-4 rounded-full font-medium text-white transition-[box-shadow,transform,background-color,opacity] duration-300 disabled:opacity-50"
                style={{
                  background: '#ff780a',
                  boxShadow: '0 4px 20px rgba(255, 120, 10, 0.3)',
                }}
                data-testid="talked-button"
              >
                We talked about this
              </motion.button>

              {/* Wavelength link */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-5"
              >
                <Link
                  to="/wavelength/new"
                  onClick={dismissHint}
                  className="text-charcoal/50 hover:text-charcoal/70 transition-colors duration-300 flex items-center gap-1.5"
                  style={{ 
                    fontFamily: "'Caveat', cursive",
                    fontSize: '1.15rem',
                    letterSpacing: '0.01em'
                  }}
                  data-testid="wavelength-share-link"
                >
                  Or share this question with Wavelength
                  <Sparkles size={14} className="text-orange-primary/70" />
                </Link>
              </motion.div>
            </>
          )}
        </motion.div>
      </main>

        {/* Cloud gradient — the "escape from cloud" transition */}
        <div
          className="pointer-events-none absolute bottom-0 left-0 right-0 z-10"
          style={{
            height: '220px',
            background: 'linear-gradient(to bottom, transparent 0%, rgba(255,248,240,0.75) 60%, #FFF8F0 100%)',
          }}
        />

        {/* (What is this? moved to fixed bottom-right — see below) */}
      </div>
      {/* ── End app viewport ────────────────────────────────────── */}

      {/* "What is this?" — desktop/tablet only, fixed bottom-right */}
      <AnimatePresence>
        {isDesktopOrTablet && showScrollHint && thought && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="fixed z-30 bottom-10 right-10"
          >
            <div className="relative inline-block">
              <Link
                to="/about"
                style={{
                  fontFamily: "'Caveat', cursive",
                  fontSize: '1.05rem',
                  color: 'rgba(44,36,23,0.32)',
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                  display: 'block',
                  lineHeight: 1.2,
                }}
                data-testid="what-is-this-link"
              >
                What is this?
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom control bar — always visible */}
      <div
        className="fixed bottom-0 left-0 right-0 p-6 md:p-8 z-20"
        data-testid="control-bar"
      >
            <div className="max-w-7xl mx-auto">
              {/* Pill selectors */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex items-end gap-3"
              >
                {/* Type selector with annotation */}
                <div className="relative">
                  <div
                    className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none"
                    style={{ bottom: '100%', marginBottom: '6px' }}
                  >
                    <span
                      style={{
                        fontFamily: "'Caveat', cursive",
                        fontSize: '1.1rem',
                        color: 'rgba(44, 36, 23, 0.45)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Who is it for?
                    </span>
                    <svg width="20" height="18" viewBox="0 0 20 18" fill="none">
                      <path d="M10 2 L10 12" stroke="rgba(44, 36, 23, 0.3)" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M6 9 L10 14 L14 9" stroke="rgba(44, 36, 23, 0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                  </div>
                  <PillSelector
                    value={type}
                    options={types}
                    onChange={handleTypeChange}
                    isActive={false}
                    testId="type-selector"
                  />
                </div>

                {/* Level selector with annotation */}
                <div className="relative">
                  <div
                    className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-none"
                    style={{ bottom: '100%', marginBottom: '6px' }}
                  >
                    <span
                      style={{
                        fontFamily: "'Caveat', cursive",
                        fontSize: '1.1rem',
                        color: 'rgba(44, 36, 23, 0.45)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      How deep?
                    </span>
                    <svg width="20" height="18" viewBox="0 0 20 18" fill="none">
                      <path d="M10 2 L10 12" stroke="rgba(44, 36, 23, 0.3)" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M6 9 L10 14 L14 9" stroke="rgba(44, 36, 23, 0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                  </div>
                  <PillSelector
                    value={level}
                    options={levels}
                    onChange={handleLevelChange}
                    isActive={false}
                    showIcon={true}
                    testId="level-selector"
                  />
                </div>
                {/* Info button */}
                <button
                  onClick={() => setShowTypeInfo(true)}
                  className="p-2 rounded-full text-charcoal/30 hover:text-charcoal/60 hover:bg-charcoal/5 transition-colors self-center"
                  data-testid="type-info-button"
                >
                  <Info size={16} />
                </button>
              </motion.div>
            </div>
          </div>
      
      {/* Toast notifications */}
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#FFF8F0',
            border: '1px solid rgba(255, 120, 10, 0.2)',
            color: '#2C2417',
          },
        }}
      />

      {/* Info Modal */}
      <InfoModal isOpen={showTypeInfo} onClose={() => setShowTypeInfo(false)} />

      {/* Value Moment — thought 4, non-blocking overlay */}
      <AnimatePresence>
        {motivation.showValueMoment && (
          <ValueMomentCard
            onDismiss={motivation.dismissValueMoment}
            talkCount={motivation.talkCount}
            currentLevel={level}
          />
        )}
      </AnimatePresence>

      {/* Soft Gate — thought 8+, requires interaction before next thought loads */}
      <AnimatePresence>
        {showSoftGate && (
          <SoftGateOverlay
            thoughtCount={motivation.thoughtCount}
            onContinue={handleSoftGatePass}
          />
        )}
      </AnimatePresence>
      </div>{/* end content-layer */}
    </div>
  );
};

export default Home;
